<template>
  <div class="card-slidedown" :class="{ 'is-open': isOpen }" :style="cardStyle">
    <div
      class="card-header"
      @click="isOpen = !isOpen"
    >
      <span class="card-title">{{ title }}</span>
      <div class="card-header-right">
        <span v-if="additional" class="card-additional">{{ additional }}</span>
        <svg
          class="card-arrow"
          :class="{ 'card-arrow--open': isOpen }"
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>
    <Transition @before-enter="onBeforeEnter" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="isOpen" class="card-body">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: null,
  },
  additional: {
    type: String,
    default: null,
  },
  renderOpen: {
    type: Boolean,
    default: false,
  },
  toggleClosed: {
    type: Boolean,
    default: null,
  },
});

const isOpen = ref(false);

// Sync isOpen from renderOpen reliably, even in compat mode
watch(() => props.renderOpen, (val) => {
  isOpen.value = val;
}, { immediate: true });

watch(() => props.toggleClosed, (newVal, oldVal) => {
  if (typeof newVal !== 'boolean' || typeof oldVal !== 'boolean') return;
  isOpen.value = !newVal;
});

const cardStyle = computed(() => {
  return props.color ? { borderLeftColor: props.color } : {};
});

function onBeforeEnter(el) {
  el.style.height = '0';
  el.style.overflow = 'hidden';
}

function onEnter(el, done) {
  el.offsetHeight;
  el.style.height = '0';
  el.style.transition = 'height 0.3s ease';
  el.style.height = el.scrollHeight + 'px';
  el.addEventListener('transitionend', done, { once: true });
}

function onAfterEnter(el) {
  el.style.height = 'auto';
  el.style.overflow = '';
  el.style.transition = '';
}

function onLeave(el, done) {
  el.style.overflow = 'hidden';
  el.style.height = el.scrollHeight + 'px';
  el.offsetHeight;
  el.style.transition = 'height 0.3s ease';
  el.style.height = '0';
  el.addEventListener('transitionend', done, { once: true });
}
</script>

<style scoped lang="scss">
.card-slidedown {
  background: #fff;
  border-radius: 4px;
  border-left: 4px solid transparent;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
}

.card-title {
  font-weight: 600;
  font-size: 1.125rem;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-additional {
  font-size: 14px;
  color: #666;
}

.card-arrow {
  transition: transform 0.3s ease;
  color: #333;

  &--open {
    transform: rotate(180deg);
  }
}

.card-body {
  padding: 0 20px 16px;
}
</style>
